import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRecipes } from '@/contexts/RecipesContext';
import { Spinner } from '@/components/Spinner';
import RecipeCard from '@/components/RecipeCard';
import Layout from '@/components/Layout';
import { Search, SlidersHorizontal, Heart, ChevronUp, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';
import { triggerLisaChat } from '@/utils/onboardingEvents';
import { supabase } from '@/integrations/supabase/client';
import { useSearchParams } from 'react-router-dom';

const RECIPES_PER_PAGE = 10;

const Recipes = () => {
  const { recipes, loading } = useRecipes();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('default');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isLisaBoxOpen, setIsLisaBoxOpen] = useState(true);
  const [displayedRecipes, setDisplayedRecipes] = useState(RECIPES_PER_PAGE);
  const isMobile = useIsMobile();
  const [recipeLikes, setRecipeLikes] = useState<Record<string, number>>({});

  // Handle URL parameters from the simplified modal
  useEffect(() => {
    const tagsParam = searchParams.get('tags');
    const sortByParam = searchParams.get('sortBy');
    
    if (tagsParam) {
      setSelectedTags([tagsParam]);
      setFiltersOpen(true); // Open filters to show the applied tag
    }
    
    if (sortByParam) {
      setSortBy(sortByParam);
    }
    
    // Clear URL params after applying them
    if (tagsParam || sortByParam) {
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Fetch recipe likes count when component mounts
  useEffect(() => {
    const fetchRecipeLikesCount = async () => {
      try {
        const { data, error } = await supabase.rpc('get_recipe_like_counts');
        
        if (error) {
          console.error('Error fetching recipe likes:', error);
          return;
        }
        
        if (data) {
          const likesMap: Record<string, number> = {};
          data.forEach((item: {recipe_id: string, count: number}) => {
            likesMap[item.recipe_id] = item.count;
          });
          setRecipeLikes(likesMap);
        }
      } catch (error) {
        console.error('Error in fetchRecipeLikesCount:', error);
      }
    };
    
    fetchRecipeLikesCount();
  }, []);

  const allTags = Array.from(
    new Set(recipes.flatMap((recipe) => recipe.tags))
  ).sort();

  const filteredRecipes = recipes.filter((recipe) => {
    const matchesSearch =
      recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipe.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.some((tag) => recipe.tags.includes(tag));

    return matchesSearch && matchesTags;
  });

  const sortedRecipes = [...filteredRecipes].sort((a, b) => {
    switch (sortBy) {
      case 'nutriscore_desc':
        return (b.nutriscore || 0) - (a.nutriscore || 0);
      case 'nutriscore_asc':
        return (a.nutriscore || 0) - (b.nutriscore || 0);
      case 'calories_asc':
        return (a.calories || 0) - (b.calories || 0);
      case 'calories_desc':
        return (b.calories || 0) - (a.calories || 0);
      case 'protein_asc':
        return (a.protein || 0) - (b.protein || 0);
      case 'protein_desc':
        return (b.protein || 0) - (a.protein || 0);
      case 'time_asc':
        return ((a.prepTime || 0) + (a.cookTime || 0)) - ((b.prepTime || 0) + (b.cookTime || 0));
      case 'time_desc':
        return ((b.prepTime || 0) + (b.cookTime || 0)) - ((a.prepTime || 0) + (a.cookTime || 0));
      case 'likes_desc':
        return (recipeLikes[b.id] || 0) - (recipeLikes[a.id] || 0);
      default:
        return 0;
    }
  });

  const handleTagSelect = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleLisaAvatarClick = () => {
    triggerLisaChat();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.4,
        type: "tween" as const
      }
    }
  };

  const visibleRecipes = sortedRecipes.slice(0, displayedRecipes);
  const hasMoreRecipes = sortedRecipes.length > displayedRecipes;

  const loadMoreRecipes = () => {
    setDisplayedRecipes(prev => Math.min(prev + RECIPES_PER_PAGE, sortedRecipes.length));
  };

  return (
    <Layout>
      <div className="py-12 bg-gradient-to-b from-white to-green-50">
        <div>
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: "tween" as const }}
            className="flex flex-col gap-2 mb-8 max-w-7xl mx-auto px-4"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-vegana-primary">
              Recetas <span className="text-vegana-secondary">Veganas</span>
            </h1>
            
            <Collapsible open={isLisaBoxOpen} onOpenChange={setIsLisaBoxOpen} className="mt-4">
              <div className="bg-white/90 backdrop-blur-md border border-purple-100 rounded-xl shadow-md p-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 h-full w-1/2 bg-gradient-to-r from-purple-100/20 to-transparent z-0"></div>
                <div className="absolute top-0 right-0 h-full w-1/2 bg-gradient-to-l from-purple-200/20 to-transparent z-0"></div>
                
                <div className="relative z-10 flex flex-col items-center gap-4">
                  <Avatar 
                    className="h-14 w-14 border-2 border-purple-300 shadow-lg inline-block bg-white cursor-pointer" 
                    onClick={handleLisaAvatarClick}
                  >
                    <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
                    <AvatarFallback>L</AvatarFallback>
                  </Avatar>
                  
                  <CollapsibleContent className="w-full">
                    <p className="text-gray-600 text-center text-sm leading-tight px-1">
                      Aquí te dejo todas las recetitas que amarás. Puedes filtrarlas muuuucho, hasta el infinito (intolerancias/alergia, ingredientes, tiempos de preparación etc..) para que solo te aparezcan las recetas que puedas preparar en tu día a día <Heart className="inline-block h-4 w-4 text-vegana-primary" />
                    </p>
                  </CollapsibleContent>
                  
                  <CollapsibleTrigger asChild className="absolute top-4 right-4">
                    <button className="p-1 hover:bg-purple-100 rounded-full">
                      {isLisaBoxOpen ? <ChevronUp className="h-4 w-4 text-purple-500" /> : <ChevronDown className="h-4 w-4 text-purple-500" />}
                    </button>
                  </CollapsibleTrigger>
                </div>
              </div>
            </Collapsible>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, type: "tween" as const }}
            className="bg-white p-6 rounded-xl shadow-md mb-8 border-t-4 border-vegana-secondary max-w-7xl mx-auto mx-4"
          >
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Buscar recetas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-200 focus-visible:ring-vegana-primary/30 transition-all"
                />
              </div>
              <div className="w-full md:w-48">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="border-gray-200 focus:ring-vegana-primary/30">
                    <SelectValue placeholder="Ordenar por..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Más relevantes</SelectItem>
                    <SelectItem value="likes_desc">Más likes</SelectItem>
                    <SelectItem value="nutriscore_desc">Nutriscore (mayor a menor)</SelectItem>
                    <SelectItem value="nutriscore_asc">Nutriscore (menor a mayor)</SelectItem>
                    <SelectItem value="calories_desc">Calorías (mayor a menor)</SelectItem>
                    <SelectItem value="calories_asc">Calorías (menor a mayor)</SelectItem>
                    <SelectItem value="protein_desc">Proteínas (mayor a menor)</SelectItem>
                    <SelectItem value="protein_asc">Proteínas (menor a mayor)</SelectItem>
                    <SelectItem value="time_desc">Tiempo total (mayor a menor)</SelectItem>
                    <SelectItem value="time_asc">Tiempo total (menor a mayor)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Collapsible
              open={filtersOpen}
              onOpenChange={setFiltersOpen}
              className="w-full"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-500">Filtrar por etiqueta</h3>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="px-2 flex items-center gap-1 text-vegana-primary">
                    <SlidersHorizontal size={16} />
                    <span className="text-sm">{filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'}</span>
                  </Button>
                </CollapsibleTrigger>
              </div>
              
              <CollapsibleContent className="mt-3">
                <motion.div 
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: { opacity: 1, height: 'auto' },
                    hidden: { opacity: 0, height: 0 }
                  }}
                  transition={{ duration: 0.3, type: "tween" as const }}
                  className="flex flex-wrap gap-2"
                >
                  {allTags.map((tag) => (
                    <Button
                      key={tag}
                      variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                      size="sm"
                      className={
                        selectedTags.includes(tag)
                          ? 'bg-vegana-secondary hover:bg-vegana-secondary/90 text-white'
                          : 'border-vegana-secondary text-vegana-secondary hover:bg-vegana-secondary/10'
                      }
                      onClick={() => handleTagSelect(tag)}
                    >
                      {tag}
                    </Button>
                  ))}
                </motion.div>
              </CollapsibleContent>
            </Collapsible>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : sortedRecipes.length > 0 ? (
            <div className="flex flex-col items-center gap-8 px-4">
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-6 max-w-[2000px] w-full"
              >
                {visibleRecipes.map((recipe) => (
                  <motion.div key={recipe.id} variants={itemVariants}>
                    <RecipeCard recipe={recipe} showAddToMenu />
                  </motion.div>
                ))}
              </motion.div>

              {hasMoreRecipes && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <Button 
                    onClick={loadMoreRecipes}
                    className="bg-vegana-primary hover:bg-vegana-primary/90"
                  >
                    Cargar más recetas
                  </Button>
                </motion.div>
              )}
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, type: "tween" as const }}
              className="text-center py-12 bg-white rounded-xl shadow-md p-8"
            >
              <div className="w-16 h-16 bg-green-50 rounded-full mx-auto flex items-center justify-center mb-4">
                <Search size={24} className="text-vegana-secondary" />
              </div>
              <h3 className="text-xl font-medium text-vegana-primary">
                No se encontraron recetas
              </h3>
              <p className="text-gray-500 mt-2 max-w-md mx-auto">
                Intenta con otros términos de búsqueda o filtros.
              </p>
              <Button 
                className="mt-6 bg-vegana-primary hover:bg-vegana-primary/90"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedTags([]);
                  setSortBy('default');
                }}
              >
                Reiniciar filtros
              </Button>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Recipes;
